import { memo } from 'react';
import isEqual from 'lodash/isEqual';
import { NarrativeTabs } from '../NarrativeTabs';
import { useDebouncedOnChange } from '../../hooks/useDebouncedOnChange/useDebouncedOnChange';
import type { NarrativeForm } from '../../utils/schema';
import type { PastAnnualRevenueEarnings } from '../../types';
import type { NarrativeSidebarCompany } from './narrativeSidebarFragment';
import type { CalculationBreakDownOutput } from '../Calculators';

interface Props {
  company: NarrativeSidebarCompany;
  onChange: (values: Partial<NarrativeForm>) => void;
  onSetFairValue: (fairValue: number) => void;
  readOnly: boolean;
  values: NarrativeForm;
  onReset: () => void;
  pastAnnualRevenueEarnings: PastAnnualRevenueEarnings;
  narrativeId?: string;
  calculationBreakDownOutput: CalculationBreakDownOutput;
  owner: {
    displayName: string;
    publicId: string;
  };
  narrativeType: 'AI' | 'FAIR_VALUE' | 'LEGACY' | 'USER' | 'WARREN' | null;
  initialFairValue: number;
  tabPanelsClassName?: string;
}

const NarrativeSidebarBase = ({ values, onChange, ...props }: Props) => {
  const { immediateValues, handleChange } = useDebouncedOnChange({
    values: values,
    onChangePartial: onChange,
  });
  return (
    <div className="grid h-min bg-surface-1 pt-x1 lg:rounded-x2">
      <NarrativeTabs
        values={immediateValues}
        onChange={handleChange}
        {...props}
      />
    </div>
  );
};

/**
 * Omits content and title from values to prevent unnecessary re-renders
 * (changes when users type inside the editor or header)
 */
const cleanProps = (props: Props) => {
  const { content, title, ...values } = props.values;
  return {
    ...props,
    values,
  };
};

export const NarrativeSidebar = memo(
  NarrativeSidebarBase,
  (prevProps, nextProps) =>
    isEqual(cleanProps(prevProps), cleanProps(nextProps))
);
